<template>
  <router-link v-if="!!to" :to="to" :class="[baseClasses, variantClasses]">
    <slot name="icon-before"></slot>
    <slot></slot>
    <slot name="icon-after"></slot>
  </router-link>
  <a v-else-if="!!href" :href="href" :class="[baseClasses, variantClasses]">
    <slot name="icon-before"></slot>
    <slot></slot>
    <slot name="icon-after"></slot>
  </a>
  <button
    v-else
    :type="type"
    :class="[baseClasses, variantClasses]"
    :disabled="disabled || loading"
  >
    <template v-if="loading">
      {{loadingText}}
      <IconLoader :disabled="disabled" />
    </template>
    <template v-else>
      <slot name="icon-before"></slot>
      <slot></slot>
      <slot name="icon-after"></slot>
    </template>
  </button>
</template>

<script>
import IconLoader from '@/components/icons/IconLoader';

export default {
  name: 'Button',
  components: {
    IconLoader,
  },
  props: {
    loadingText: {
      type: String,
      required: false,
      default: "Laddar"
    },
    href: {
      type: String,
      required: false,
    },
    to: {
      type: Object,
      required: false,
    },
    variant: {
      type: String,
      validator: function (value) {
        return ['primary', 'secondary', 'success', 'danger', 'tertiary', 'text', 'social', 'secondarywhite', 'secondarysearch', 'primarygreen'].includes(
          value,
        );
      },
      default: 'primary',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      validator: function (value) {
        return ['small', 'large', 'extralarge'].includes(value);
      },
      default: 'small',
    },
    type: {
      type: String,
      default: 'button',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    state: {
      type: String,
      validator: function (value) {
        return ['default', 'active', 'cannot-remove'].includes(value);
      },
      default: 'default',
    },
  },
  computed: {
    baseClasses() {
      if(this.variant != "round") return 'inline-flex whitespace-nowrap font-bold flex-nowrap items-center gap-2 rounded outline-none justify-center';
      else return ''
    },
    variantClasses() {
      return {
        'bg-turquoise-10 rounded-[50%] h-auto px-[12px] py-[12px]':
          this.variant === 'round',
        'bg-turquoise-60 text-white focus-within:bg-turquoise-80 enabled:hover:bg-turquoise-80 disabled:bg-gray-20 disabled:text-[#5E5E5E]':
          this.variant === 'primary',
        'bg-green-dark text-white focus-within:bg-turquoise-30 hover:bg-green-dark disabled:bg-gray-20 disabled:text-[#5E5E5E]':
          this.variant === 'primarygreen',
        'bg-turquoise-10 text-turquoise-60 focus-within:bg-turquoise-30 hover:bg-turquoise-30 disabled:bg-gray-20 disabled:text-[#5E5E5E]':
          this.variant === 'secondary',
        'bg-turquoise-10 text-turquoise-60 focus-within:border-turquoise-60 border border-turquoise-10 hover:border-solid enabled:hover:border-turquoise-60 disabled:bg-gray-20 disabled:text-[#5E5E5E]':
          this.variant === 'secondarysearch',
        'bg-white text-turquoise-60 border border-solid border-dark-turquoise focus-visible:border-none focus-visible:outline-2 focus-visible:outline-[#E50063] focus-visible:rounded focus-within:bg-turquoise-10 hover:bg-turquoise-10 disabled:bg-gray-20 disabled:text-[#5E5E5E]':
          this.variant === 'secondarywhite',
        'bg-danger-10 text-wine-red focus-within:bg-wine-red focus-within:text-danger-10 hover:bg-wine-red hover:text-danger-10 disabled:bg-gray-20 disabled:text-gray-50':
          this.variant === 'danger',
        'bg-success-10 text-success focus-within:bg-success hover:bg-success disabled:bg-gray-20 hover:text-success-10 disabled:text-gray-50':
          this.variant === 'success',
        'bg-transparent text-turquoise-60 focus-within:bg-transparent hover:bg-turquoise-20 focus-visible:rounded focus-visible:border-[3px] focus-visible:border-black focus-visible:bg-[#CCE8EE] disabled:text-gray-20 disabled:text-gray-50':
          this.variant === 'tertiary',
        'bg-transparent text-turquoise-60 outline-none ring-transparent hover:underline focus-within:underline disabled:text-gray-50 p-0 [&_svg]:w-5 [&_svg]:h-5 [&_svg_path]:fill-current':
          this.variant === 'text',
        'block w-full rounded-[4px] border focus:outline-none h-11 text-lg px-4 border-solid flex items-center gap-3 !justify-start':
          this.variant === 'social',
        'border-gray-20 enabled:hover:bg-gray-30 enabled:hover:border-gray-30 text-[#000]':
          this.variant === 'social' && this.state === 'default',
        'flex w-full items-center gap-3 bg-black text-white enabled:hover:bg-gray-30 enabled:hover:border-gray-30 hover:text-black group':
          this.variant === 'social' && this.state === 'active',
        'block w-full rounded-[4px] border bg-gray-20 focus:outline-none border-gray-20 enabled:hover:bg-gray-30 enabled:hover:border-gray-30 border-solid':
          this.variant === 'social' && this.state === 'cannot-remove',
        'px-[14px] h-11 md:px-[16px] md:h-[44px]':
          this.size === 'small' && this.variant !== 'social' && this.variant !== 'round',
        'px-[14px] h-11 md:px-[16px] md:h-11':
          this.size === 'large' && this.variant !== 'social',
        'text-[14px]':
          this.size === 'small' && this.variant == 'text',
        'px-[24px] md:px-[32px] h-14 rounded-[6px] text-[16px] md:text-[18px]':
          this.size === 'extralarge',
      };
    },
  },
};
</script>
